import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { encodeSop, parseSop } from '../lib/chat/sopFormat';
import { agentClient } from '../lib/connect/clients';
export const SOPS_KEY = 'sops';
const LAYER_BY_SCOPE = {
    builtin: 'built-in',
    tenant: 'organization',
    user: 'personal'
};
const RANK = {
    personal: 3,
    organization: 2,
    'built-in': 1
};
/** The layer an SOP with this name and layer overrides, if a lower one exists. */ export function overriddenLayer(row, rows) {
    let best = null;
    for (const r of rows){
        if (r.name !== row.name || RANK[r.layer] >= RANK[row.layer]) continue;
        if (!best || RANK[r.layer] > RANK[best]) best = r.layer;
    }
    return best;
}
/** True when a higher layer has an SOP with the same name, so this one does not apply. */ export function isShadowed(row, rows) {
    return rows.some((r)=>r.name === row.name && RANK[r.layer] > RANK[row.layer]);
}
async function fetchSops() {
    const resp = await agentClient.listMemories({
        scopeKind: 'sop'
    });
    const rows = [];
    for (const m of resp.memories ?? []){
        const layer = LAYER_BY_SCOPE[m.scopeKind];
        if (!m.id || !layer) continue;
        rows.push({
            ...parseSop(m.body, m.title),
            id: m.id,
            layer,
            updatedAt: m.updatedAt?.toDate()
        });
    }
    rows.sort((a, b)=>a.name.localeCompare(b.name) || RANK[b.layer] - RANK[a.layer]);
    return rows;
}
export function useSops(enabled = true) {
    return useQuery({
        queryKey: [
            SOPS_KEY
        ],
        queryFn: fetchSops,
        enabled
    });
}
export function useSaveSop() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>agentClient.upsertMemory({
                id: input.id ?? '',
                kind: 'sop',
                scopeKind: input.scope === 'organization' ? 'tenant' : 'user',
                title: input.name,
                body: encodeSop(input)
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    SOPS_KEY
                ]
            })
    });
}
export function useDeleteSop() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>agentClient.deleteMemory({
                id
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    SOPS_KEY
                ]
            })
    });
}
